// @ts-check

/**
 * @typedef {import("./storefront-types").NavigationItem} NavigationItem
 * @typedef {import("./storefront-types").CategorySummary} CategorySummary
 * @typedef {import("./storefront-shell-types").MergedMenuCategory} MergedMenuCategory
 * @typedef {import("./storefront-shell-types").BreadcrumbItem} BreadcrumbItem
 * @typedef {import("./storefront-shell-types").HeaderNavigationContentEntry} HeaderNavigationContentEntry
 * @typedef {import("./storefront-shell-types").HeaderNavigationActionEntry} HeaderNavigationActionEntry
 * @typedef {import("./storefront-shell-types").HeaderNavigationEntry} HeaderNavigationEntry
 */

const SETTINGS_LABEL = "settings";
const HOME_LABEL = "home";
const ACCOUNT_LINK_KEY = "account-link";

/**
 * @param {string | undefined | null} path
 */
export function normalizePath(path) {
  if (!path) {
    return "/";
  }

  const source = String(path).trim();
  let candidate = source;

  if (/^https?:\/\//i.test(source)) {
    try {
      candidate = new URL(source).pathname;
    } catch {
      candidate = source;
    }
  }

  const withoutHash = candidate.split("#", 1)[0];
  const withoutQuery = withoutHash.split("?", 1)[0];
  const normalized = withoutQuery.endsWith("/") && withoutQuery !== "/"
    ? withoutQuery.slice(0, -1)
    : withoutQuery;

  return normalized || "/";
}

/**
 * @param {string | undefined | null} value
 */
function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * @param {string} path
 */
function getLastSegment(path) {
  const segments = normalizePath(path).split("/").filter(Boolean);
  return segments.at(-1) || "";
}

/**
 * @param {CategorySummary} category
 */
function toInternalCategoryPath(category) {
  const normalizedUrl = normalizePath(category.url);
  if (normalizedUrl !== "/") {
    return normalizedUrl;
  }

  return category.slug ? `/menu/${category.slug}` : "/menu";
}

/**
 * @param {string} segment
 */
function formatSegment(segment) {
  return decodeURIComponent(segment)
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

/**
 * @param {NavigationItem} item
 */
function isSettingsNavigationItem(item) {
  const label = slugify(item.label);
  const path = normalizePath(item.path);

  return (
    label === SETTINGS_LABEL ||
    path === "/settings" ||
    path.startsWith("/settings/")
  );
}

/**
 * @param {NavigationItem} item
 */
function isHomeNavigationItem(item) {
  const label = slugify(item.label);
  const path = normalizePath(item.path);

  return label === HOME_LABEL || path === "/";
}

/**
 * @param {NavigationItem[]} items
 * @param {string} pathname
 * @returns {NavigationItem[]}
 */
function findNavigationTrail(items, pathname) {
  for (const item of items) {
    const childTrail = findNavigationTrail(item.children || [], pathname);
    if (childTrail.length > 0) {
      return [item, ...childTrail];
    }

    if (normalizePath(item.path) === pathname) {
      return [item];
    }
  }

  return [];
}

/**
 * @param {NavigationItem} item
 */
export function isMenuNavigationItem(item) {
  return normalizePath(item.path) === "/menu" || slugify(item.label) === "menu";
}

/**
 * @param {NavigationItem[]} items
 * @returns {NavigationItem[]}
 */
export function filterHeaderNavigation(items) {
  return (items || [])
    .filter((item) => !isSettingsNavigationItem(item) && !isHomeNavigationItem(item))
    .map((item) => ({
      ...item,
      children: filterHeaderNavigation(item.children || []),
    }));
}

/**
 * @param {number | null | undefined} loginRegisterInsertAfter
 * @param {number} itemCount
 */
function resolveLoginRegisterInsertIndex(loginRegisterInsertAfter, itemCount) {
  const insertAfter = Number(loginRegisterInsertAfter);

  if (
    !Number.isInteger(insertAfter) ||
    insertAfter < 1 ||
    insertAfter > itemCount
  ) {
    return itemCount;
  }

  return insertAfter;
}

/**
 * @param {NavigationItem[]} items
 * @param {number | null | undefined} loginRegisterInsertAfter
 * @param {boolean} [signedIn]
 * @returns {HeaderNavigationEntry[]}
 */
export function buildHeaderNavigationEntries(
  items,
  loginRegisterInsertAfter,
  signedIn = false,
) {
  /** @type {HeaderNavigationContentEntry[]} */
  const contentEntries = filterHeaderNavigation(items).map((item) => ({
    kind: "content",
    key: item.key,
    item,
  }));
  const insertIndex = resolveLoginRegisterInsertIndex(
    loginRegisterInsertAfter,
    contentEntries.length,
  );

  return [
    ...contentEntries.slice(0, insertIndex),
    {
      kind: "action",
      key: ACCOUNT_LINK_KEY,
      label: signedIn ? "Account" : "Login",
      path: signedIn ? "/account" : "/sign-in",
    },
    ...contentEntries.slice(insertIndex),
  ];
}

/**
 * @param {HeaderNavigationEntry[]} entries
 * @returns {{
 *   quickLinks: HeaderNavigationContentEntry[];
 *   actions: HeaderNavigationActionEntry[];
 * }}
 */
export function splitMobileNavigationEntries(entries) {
  /** @type {HeaderNavigationContentEntry[]} */
  const quickLinks = [];
  /** @type {HeaderNavigationActionEntry[]} */
  const actions = [];

  for (const entry of entries || []) {
    if (entry.kind === "action") {
      actions.push(entry);
      continue;
    }

    if (!isMenuNavigationItem(entry.item)) {
      quickLinks.push(entry);
    }
  }

  return { quickLinks, actions };
}

/**
 * @param {NavigationItem} item
 * @param {string} pathname
 * @returns {boolean}
 */
export function hasActivePath(item, pathname) {
  if (normalizePath(item.path) === normalizePath(pathname)) {
    return true;
  }

  return (item.children || []).some((child) => hasActivePath(child, pathname));
}

/**
 * @param {NavigationItem[]} items
 * @param {CategorySummary[]} categories
 * @returns {MergedMenuCategory[]}
 */
export function mergeMenuCategories(items, categories) {
  const headerItems = filterHeaderNavigation(items);
  const menuItem = headerItems.find((item) => isMenuNavigationItem(item));

  if (!menuItem) {
    return [];
  }

  const categoriesByPath = new Map();
  const categoriesBySlug = new Map();
  const categoriesByLabel = new Map();

  for (const category of categories || []) {
    const categoryPath = toInternalCategoryPath(category);
    categoriesByPath.set(normalizePath(categoryPath), category);
    categoriesBySlug.set(slugify(category.slug || getLastSegment(categoryPath)), category);
    categoriesByLabel.set(slugify(category.name), category);
  }

  /** @type {Set<string>} */
  const usedSlugs = new Set();
  /** @type {MergedMenuCategory[]} */
  const mergedCategories = [];

  for (const child of menuItem.children || []) {
    const childPath = normalizePath(child.path);
    const matchedCategory = categoriesByPath.get(childPath) ||
      categoriesBySlug.get(slugify(getLastSegment(childPath))) ||
      categoriesByLabel.get(slugify(child.label));

    if (matchedCategory?.slug) {
      usedSlugs.add(matchedCategory.slug);
    }

    mergedCategories.push({
      key: child.key,
      label: matchedCategory?.name || child.label,
      path: childPath === "/" && matchedCategory
        ? toInternalCategoryPath(matchedCategory)
        : childPath,
      summary: matchedCategory?.summary || "",
      image: matchedCategory?.image || null,
    });
  }

  for (const category of categories || []) {
    if (category.slug && usedSlugs.has(category.slug)) {
      continue;
    }

    mergedCategories.push({
      key: `menu-category:${category.slug}`,
      label: category.name,
      path: toInternalCategoryPath(category),
      summary: category.summary,
      image: category.image || null,
    });
  }

  return mergedCategories;
}

/**
 * @param {{
 *   pathname: string;
 *   navigationItems: NavigationItem[];
 *   currentPageTitle?: string;
 *   menuCategories?: CategorySummary[];
 * }} options
 * @returns {BreadcrumbItem[]}
 */
export function buildBreadcrumbs({
  pathname,
  navigationItems,
  currentPageTitle,
  menuCategories,
}) {
  const normalizedPath = normalizePath(pathname);

  if (normalizedPath === "/") {
    return [];
  }

  const headerItems = filterHeaderNavigation(navigationItems);
  const trail = findNavigationTrail(headerItems, normalizedPath);
  const title = currentPageTitle?.trim();

  if (trail.length > 0) {
    return trail.map((item, index) => ({
      label: index === trail.length - 1 && title ? title : item.label,
      path: index === trail.length - 1 ? null : normalizePath(item.path),
    }));
  }

  const segments = normalizedPath.split("/").filter(Boolean);
  if (segments.length === 0) {
    return [];
  }

  if (segments[0] === "menu") {
    if (segments.length === 1) {
      return [{ label: title || "Menu", path: null }];
    }

    /** @type {BreadcrumbItem[]} */
    const crumbs = [{ label: "Menu", path: "/menu" }];

    for (let index = 1; index < segments.length - 1; index += 1) {
      const segment = segments[index];
      const category = (menuCategories || []).find(
        (candidate) => slugify(candidate.slug) === slugify(segment),
      );

      crumbs.push({
        label: category?.name || formatSegment(segment),
        path: `/menu/${segments.slice(1, index + 1).join("/")}`,
      });
    }

    crumbs.push({
      label: title || formatSegment(segments.at(-1) || "menu"),
      path: null,
    });

    return crumbs;
  }

  let currentPath = "";
  return segments.map((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === segments.length - 1;

    return {
      label: isLast && title ? title : formatSegment(segment),
      path: isLast ? null : currentPath,
    };
  });
}
